import React, { useEffect, useState } from 'react';
import Box from 'web-library/stories/atom/Box/Box';
import Grid from 'web-library/stories/atom/Grid/Grid';
import SectionHeader from 'web-library/stories/molecule/SectionHeader/SectionHeader';
import TextFieldForm from 'web-library/stories/atom/TextField/TextFieldForm';
import HtmlTrans from 'web-library/stories/atom/HtmlTrans/HtmlTrans';
import {
  DateOnlySemanticDisplayFormat,
  nameRegExp,
  yesterdayDate,
  DatePicker
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { joiResolver } from '@hookform/resolvers/joi';
import { useForm, Controller } from 'react-hook-form';
import { MutationStatus } from 'react-query';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';

import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../../models';
import schemaVerifyCandidate from '../../schema/schemaVerifyCandidate';
import { ID_TYPE_MENU_ITEMS } from '../../logic/useCreateCandidateForm';
import ProfileDetailsComponents from './ProfileDetailsComponents';
import { useGetSchoolsByType } from '../../../../api-refactored/non-national-exam-registration/useGetSchoolsByType';
import { LookupListItem } from '../../../../api/user/model';
import dayjs from 'dayjs';
// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) => ({
  statement: {
    marginBottom: theme.spacing(4),
  },
  textField: {
    paddingBottom: theme.spacing(3),
  },
  errorAsterisk: {
    color: theme.palette.error.main,
  },
  errorAsteriskOnDisabled: {
    color: theme.color.text.textDisabled,
  },
  verifyButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(7),
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
    },
  },
  nricField: {
    // Custom breakpoint value is defined to ensure that the IdField and nricField are vertically aligned.
    [theme.breakpoints.between('md', 1184)]: {
      marginTop: theme.spacing(12),
    },
    [theme.breakpoints.up(1184)]: {
      marginTop: theme.spacing(9),
    },
  },
}));

const defaultDOBPickerProps = {
  placeholder: 'Enter date of birth',
  disableFuture: true,
  format: DateOnlySemanticDisplayFormat,
};

interface ProfileDetailsProps {
  onCallVerifyCandidate?: (formData: CandidateProfileFormData) => void;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isDisableValidationForDisabledInputs?: boolean;
  submitError?: string;
  verifyCandidateSubmitError?: string;
  checkVerifyButtonStatus?: (index: MutationStatus) => void;
  setProfileData: (formData: CandidateProfileFormData) => void;
  verifyCandidateStatus: MutationStatus;
  errorTime: number;
  isSingPassUser?: boolean;
}

const setInvalidPrStatusMsg = (
  t: TFunction<'translation'>,
  isSingPassUser: boolean,
  isDisableInputs: CandidateProfileIsDisableInputs | undefined,
  formDefaultValues: CandidateProfileFormDefaultValues | undefined
) => {
  const prStatusDisabled = isSingPassUser && isDisableInputs?.prStatus;
  const blankPrStatus = isSingPassUser && !formDefaultValues?.prStatus;
  if (prStatusDisabled && blankPrStatus) {
    return t('profile->>CreateCandidateForm.MyInfoPrStatus');
  }
  return undefined;
};

const isDisableNameField = (
  verifyButtonStatusSuccess: boolean,
  isDisableInputs?: CandidateProfileIsDisableInputs
) => isDisableInputs?.name || verifyButtonStatusSuccess;

const isDisableNricField = (
  verifyButtonStatusSuccess: boolean,
  isDisableInputs?: CandidateProfileIsDisableInputs
) => isDisableInputs?.NricFin || verifyButtonStatusSuccess;

const ProfileDetails = ({
  onCallVerifyCandidate = () => {
    // do nothing
  },
  formDefaultValues,
  isDisableInputs,
  isDisableValidationForDisabledInputs = false,
  verifyCandidateSubmitError = '',
  checkVerifyButtonStatus = () => {
    // do nothing
  },
  setProfileData,
  verifyCandidateStatus,
  errorTime,
  isSingPassUser = false,
}: ProfileDetailsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [invalidPrStatus] = useState<string | undefined>(
    setInvalidPrStatusMsg(t, isSingPassUser, isDisableInputs, formDefaultValues)
  );

  const formHook = useForm<CandidateProfileFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      schemaVerifyCandidate(isDisableValidationForDisabledInputs ? isDisableInputs : {})
    ),
    defaultValues: formDefaultValues ?? { dateOfBirth: '' },
  });

  const { control, formState: { errors }, handleSubmit, setValue } = formHook;

  const [verifyButtonStatus, setVerifyButtonStatus] = useState<MutationStatus>('idle');
  const verifyButtonStatusSuccess = verifyButtonStatus === 'success';

  const {
    data: dataOrganisation,
    isLoading: isLoadingOrganisation,
    error: errorOrganisation,
    refetch: refetchOrganisation,
  } = useGetSchoolsByType(true, 'NMOE');

  useEffect(() => {
    setVerifyButtonStatus(verifyCandidateStatus);
    checkVerifyButtonStatus(verifyCandidateStatus);
  }, [verifyCandidateStatus, checkVerifyButtonStatus]);

  const verifyCandidate = handleSubmit((formData) => {
    onCallVerifyCandidate(formData);
    setProfileData(formData);
  });

  return (
    <>
      <SectionHeader title={t('profile->>CreateCandidateForm.ProfileDetails')} />
      <Box className={classes.statement}>
        {t('profile->>CreateCandidateForm.VerifyCandidateEligibleFillInForm')}
      </Box>
      <form onSubmit={verifyCandidate}>
        <TextFieldForm
          form={formHook}
          isInputTextUpperCase
          onChange={(e) => setValue('name', e.target.value?.toUpperCase())}
          label={t('profile->>CreateCandidateForm.Name')}
          isRequired
          name="name"
          type="text"
          id="candidateName"
          placeholder="Enter candidate name"
          defaultValue={formDefaultValues?.name}
          size="medium"
          className={classes.textField}
          error={!!errors.name}
          helperText={errors?.name?.message?.toString()}
          fullWidth
          inputProps={{
            readOnly: isDisableNameField(verifyButtonStatusSuccess, isDisableInputs),
          }}
          regExpToBlock={nameRegExp}
        />
        <Grid container spacing={4} direction="row" className={classes.textField}>
          <Grid md={6} sm={12} xs={12}>
            <ControlledAutocomplete<
              { key: string; value: string; children: string },
              false,
              false,
              undefined
            >
              label={t('profile->>CreateCandidateForm.IDType')}
              isRequired
              control={control}
              name="IdType"
              id="inputSelect-idType"
              error={!!errors.IdType}
              helperText={errors?.IdType?.message?.toString()}
              options={ID_TYPE_MENU_ITEMS}
              getOptionLabel={({ children }) => children}
              placeholder="Select ID type"
              disabled={isDisableInputs?.IdType || verifyButtonStatus === 'success'}
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.value === selectedOption) ?? null,
                output: (selectedOption) => (selectedOption ? selectedOption.value : null),
              }}
              isSubLabelBelowLabel
              subLabel={
                <>
                  <HtmlTrans
                    i18nKey="profile->>CreateCandidateForm.IdHelpText"
                    values={{
                      boldContent: 'only if',
                    }}
                  />
                  <br />
                  <>{t('profile->>CreateCandidateForm.IdHelpText2')}</>
                </>
              }
            />
          </Grid>
          <Grid className={classes.nricField} md={6} sm={12} xs={12}>
            <TextFieldForm
                form={formHook}
              label={t('profile->>CreateCandidateForm.NricFinPassportNo')}
              isRequired
              name="NricFin"
              type="text"
              id="inputText-candidate-nric-fin-pass-no"
              placeholder="Enter NRIC/FIN/Passport No."
              defaultValue={formDefaultValues?.NricFin}
              size="medium"
              error={!!errors.NricFin}
              helperText={errors?.NricFin?.message?.toString()}
              onChange={(e) => setValue('NricFin', e.target.value.toUpperCase())}
              fullWidth
              inputProps={{
                readOnly: isDisableNricField(verifyButtonStatusSuccess, isDisableInputs),
              }}
            />
          </Grid>
        </Grid>
        <Controller
            name="dateOfBirth"
            control={control}
            render={({field: {onChange, ref, ...restField}}) => (
                <DatePicker
                    label={t('profile->>CreateCandidateForm.DateOfBirth')}
                    isRequired
                    defaultValue={formDefaultValues?.dateOfBirth}
                    onChange={(date) => onChange(date)}
                    inputVariant="filled"
                    id="dateOfBirth"
                    inputRef={ref}
                    error={!!errors.dateOfBirth}
                    helperText={errors?.dateOfBirth?.message?.toString()}
                    className={classes.textField}
                    maxDate={dayjs(yesterdayDate)}
                    disabled={isDisableInputs?.dateOfBirth || verifyButtonStatus === 'success'}
                    {...defaultDOBPickerProps}
                    {...restField}
                />
            )}
        />
        <Box className={classes.textField}>
          <ControlledAutocomplete<LookupListItem, false, false, undefined>
            label={t('profile->>CreateCandidateForm.School')}
            isRequired
            control={control}
            name="school"
            id="input-select-school"
            error={!!errors.school}
            helperText={errors?.school?.message?.toString()}
            loading={isLoadingOrganisation}
            loadingError={!!errorOrganisation}
            loadingErrorOnReFetch={refetchOrganisation}
            options={dataOrganisation || []}
            getOptionLabel={({ name }) => name}
            placeholder="Select school"
            disabled={isDisableInputs?.school || verifyButtonStatus === 'success'}
            isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
            isSubLabelBelowLabel
            subLabel={
              <>
                <>{t('profile->>CreateCandidateForm.SchoolHelpTextPartOne')}</>
                <HtmlTrans
                  i18nKey="profile->>CreateCandidateForm.SchoolHelpTextPartTwo"
                  values={{
                    boldWord: 'NOT',
                  }}
                />
              </>
            }
          />
        </Box>
        <ProfileDetailsComponents
          verifyCandidateSubmitError={verifyCandidateSubmitError}
          invalidPrStatus={invalidPrStatus}
          verifyButtonStatus={verifyButtonStatus}
          classes={classes}
          errorTime={errorTime}
        />
      </form>
    </>
  );
};

export default ProfileDetails;
